import { isValid } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

import { DatePickerRange } from '@/domain/requestsPage';
import { formatDateForCalendarView, getLocaleDateValidationRegex, parseDateFromLocalFormat } from '@/utils/date/date';

const clampEndDate = (startDate: Date | null | undefined, endDate: Date | null | undefined): Date | null => {
    if (startDate && endDate && endDate < startDate) return startDate;
    return endDate ?? null;
};

const useDateRangeInputLogic = ({
    dateRange,
    onChange,
    name,
}: {
    name: string;
    dateRange: DatePickerRange;
    onChange: (name: string, dateRange: DatePickerRange) => void;
}) => {
    const [startDate, setStartDate] = useState<string | null>('');
    const [endDate, setEndDate] = useState<string | null>('');

    const [isStartDateValid, setIsStartDateValid] = useState(true);
    const [isEndDateValid, setIsEndDateValid] = useState(true);

    useEffect(() => {
        const { startDate, endDate } = dateRange;

        isValid(startDate) ? setStartDate(formatDateForCalendarView(startDate as Date) || '') : setStartDate('');
        isValid(endDate) && endDate ? setEndDate(formatDateForCalendarView(endDate) || '') : setEndDate('');

        setIsStartDateValid(true);
        setIsEndDateValid(true);
    }, [dateRange]);

    const onStartDateChange = useCallback(event => {
        setStartDate(event.target.value);
    }, []);

    const onEndDateChange = useCallback(event => {
        setEndDate(event.target.value);
    }, []);

    const onStartDateBlur = useCallback(() => {
        if (!startDate) {
            setIsStartDateValid(true);
            return;
        }

        const validDateRegex = getLocaleDateValidationRegex();
        const isValidFormat = validDateRegex.test(startDate);

        setIsStartDateValid(isValidFormat);

        if (!isValidFormat) return;

        const parsedDate = parseDateFromLocalFormat(startDate);
        if (!parsedDate) {
            setIsStartDateValid(false);
            return;
        }

        const effectiveEndDate = clampEndDate(parsedDate, dateRange?.endDate);

        onChange(name, { startDate: parsedDate, endDate: effectiveEndDate });
    }, [dateRange?.endDate, name, onChange, startDate]);

    const onEndDateBlur = useCallback(() => {
        if (!endDate) {
            setIsEndDateValid(true);
            return;
        }

        const validDateRegex = getLocaleDateValidationRegex();
        const isValidFormat = validDateRegex.test(endDate);

        setIsEndDateValid(isValidFormat);

        if (!isValidFormat) return;

        const parsedDate = parseDateFromLocalFormat(endDate);
        if (!parsedDate) {
            setIsEndDateValid(false);
            return;
        }

        const effectiveEndDate = clampEndDate(dateRange?.startDate, parsedDate);

        onChange(name, { startDate: dateRange?.startDate, endDate: effectiveEndDate });
    }, [dateRange?.startDate, endDate, name, onChange]);

    return {
        startDate,
        endDate,
        isStartDateValid,
        isEndDateValid,
        onStartDateChange,
        onEndDateChange,
        onStartDateBlur,
        onEndDateBlur,
    };
};

export default useDateRangeInputLogic;
